import { XIcon } from "@phosphor-icons/react";
import {
  type NavigateOptions,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { BackButton } from "@/components/BackButton/BackButton";
import { IconButton } from "@/components/IconButton/IconButton";
import { ProgressStepper } from "@/components/ProgressStepper/ProgressStepper";
import { CancelHotelBooking } from "@/features/hotels/components/hotel/CancelHotelBooking";
import { HotelSearchForm } from "@/features/hotels/components/HotelSearchForm/HotelSearchForm";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import type { HotelBookingStepNumber } from "@/features/hotels/types/types";
import { formatHeaderDate } from "@/utils/dates";

type HotelBookingHeaderProps = {
  className?: string;
  title?: ReactNode;
  onClose?: () => void;
  showStepper?: boolean;
  showSearchForm?: boolean;
} & (
  | {
      showStepper: true;
      step: HotelBookingStepNumber;
    }
  | { showStepper?: false }
);

const stepTitles = defineMessages<HotelBookingStepNumber>({
  0: { id: "hotels.title", defaultMessage: "Hotels" },
  1: { id: "hotels.chooseRoom", defaultMessage: "Choose a Room" },
  2: { id: "travellers.title", defaultMessage: "Travellers" },
  3: { id: "summary", defaultMessage: "Summary" },
  4: { id: "success", defaultMessage: "Success" },
});

const STEPS_CONFIG: Record<HotelBookingStepNumber, { prev?: NavigateOptions }> =
  {
    0: {},
    1: {},
    2: {},
    3: {},
    4: {
      prev: { to: "/" },
    },
  };

export const HotelBookingHeader = ({
  title,
  onClose,
  showSearchForm,
  ...props
}: HotelBookingHeaderProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { history } = useRouter();

  const { searchData, updateSearchData } = useHotelSearchDataContext();
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  return (
    <>
      <div className="mb-4 flex w-full flex-col px-2">
        <div className="flex items-center justify-between">
          {props.showStepper ? (
            <BackButton
              onClick={() => {
                const prevOptions = STEPS_CONFIG[props.step].prev;
                if (prevOptions) {
                  navigate(prevOptions);
                } else {
                  history.back();
                }
              }}
            />
          ) : (
            <div className="size-6" />
          )}
          <div className="flex flex-col items-center justify-center gap-1">
            <div className="flex w-full items-center justify-center">
              {props.showStepper ? (
                <h3 className="font-medium text-text-title">
                  {intl.formatMessage(stepTitles[props.step])}
                </h3>
              ) : (
                title
              )}
            </div>
            {searchData ? (
              <span className="max-w-[220px] truncate text-xs font-normal text-text-tertiary">
                {formatHeaderDate(searchData.dates.checkIn)} -{" "}
                {formatHeaderDate(searchData.dates.checkOut)},{" "}
                <span>
                  {intl.formatMessage(
                    {
                      id: "hotels.search.roomCount",
                      defaultMessage:
                        "{count, plural, one {# room} other {# rooms}}",
                    },
                    {
                      count: searchData.rooms.length,
                    },
                  )}
                </span>
              </span>
            ) : null}
          </div>
          <IconButton
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                setIsCancelConfirmOpen(true);
              }
            }}
          >
            <XIcon size={24} color="text-text-title" />
          </IconButton>
        </div>

        {showSearchForm ? (
          <div className="mx-2 mt-4 rounded-4xl bg-white p-3 shadow-sm">
            <HotelSearchForm
              variant="compact"
              initialValues={{
                dates: searchData!.dates,
                rooms: searchData!.rooms,
                destination: null,
              }}
              analyticsSource="sticky"
              onChange={(values) => {
                updateSearchData((prev) => {
                  if (!prev) {
                    return null;
                  }

                  return {
                    ...prev,
                    dates: values.dates,
                    rooms: values.rooms,
                  };
                });
              }}
            />
          </div>
        ) : null}

        {props.showStepper ? (
          <div className="my-2 px-2">
            <ProgressStepper
              totalSteps={5} // 0 is a valid step so we need to add 1
              currentStepNumber={props.step + 1}
            />
          </div>
        ) : null}
      </div>

      <CancelHotelBooking
        open={isCancelConfirmOpen}
        closeDrawer={() => setIsCancelConfirmOpen(false)}
      />
    </>
  );
};
